import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../auth/supabase';
import { apiFetch } from '../api/client';
import styles from './PatientDetail.module.css';
import PlanWizard from '../components/PlanWizard';
import PlanView from '../components/PlanView';
import PlanItemsEditor from '../components/PlanItemsEditor';
import AssetsPanel from '../components/AssetsPanel';
import GeneratePanel from '../components/GeneratePanel';

export default function PatientDetail() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState('');
  const [plansLoading, setPlansLoading] = useState(true);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'info';
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch(`/patients/${id}`, {}, getToken);
        setPatient(data);
        setForm({
          full_name: data.full_name || '',
          mrn: data.mrn || '',
          age: data.age || '',
          sex: data.sex || 'M',
          chief_complaint: data.chief_complaint || '',
          chief_complaint_hx: data.chief_complaint_hx || '',
          medical_hx: data.medical_hx || '',
          dental_hx: data.dental_hx || '',
          nationality: data.nationality || '',
          occupation: data.occupation || '',
          marital_status: data.marital_status || '',
        });
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      setPlansLoading(true);
      setPlansError('');
      try {
        const data = await apiFetch(`/patients/${id}/plans`, {}, getToken);
        setPlans(data);
      } catch (e) {
        setPlansError(e.message);
      } finally {
        setPlansLoading(false);
      }
    })();
  }, [id]);

  const activePlanId = useMemo(() => (plans[0]?.id || null), [plans]);
  const [planMeta, setPlanMeta] = useState({ starting_date: '', academic_level: '', case_number: '' });
  const [planEditing, setPlanEditing] = useState(false);

  useEffect(() => {
    const m = (plans[0]?.meta) || {};
    setPlanMeta({
      starting_date: m.starting_date || '',
      academic_level: m.academic_level || '',
      case_number: m.case_number || '',
    });
  }, [plans]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>{patient?.full_name || 'Patient'}</h2>
          <div className={styles.meta}>
            <span>MRN: {patient?.mrn || '-'}</span>
            <span>Age: {patient?.age ?? '-'}</span>
            <span>Sex: {patient?.sex ?? '-'}</span>
          </div>
        </div>
        <div className={styles.tabs}>
          <button className={tab==='info'?styles.tabActive:styles.tab} onClick={() => setSearchParams({ tab: 'info' })}>Info</button>
          <button className={tab==='plan'?styles.tabActive:styles.tab} onClick={() => setSearchParams({ tab: 'plan' })}>Tx Plan</button>
          <button className={tab==='assets'?styles.tabActive:styles.tab} onClick={() => setSearchParams({ tab: 'assets' })}>Assets</button>
          <button className={tab==='generate'?styles.tabActive:styles.tab} onClick={() => setSearchParams({ tab: 'generate' })}>Generate</button>
        </div>

        {tab === 'info' && (
          <div className={styles.section}>
            <h3>Overview</h3>
            {error && <div className={styles.error}>{error}</div>}
            {!patient ? (
              <div className={styles.info}>Loading…</div>
            ) : (
              <form
                className={styles.form}
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const body = {
                      full_name: form.full_name,
                      mrn: form.mrn || null,
                      age: form.age ? parseInt(form.age, 10) : null,
                      sex: form.sex || null,
                      chief_complaint: form.chief_complaint || null,
                      chief_complaint_hx: form.chief_complaint_hx || null,
                      medical_hx: form.medical_hx || null,
                      dental_hx: form.dental_hx || null,
                      nationality: form.nationality || null,
                      occupation: form.occupation || null,
                      marital_status: form.marital_status || null,
                    };
                    const updated = await apiFetch(`/patients/${id}`, { method: 'PUT', body }, getToken);
                    setPatient(updated);
                    setEditing(false);
                  } catch (e) {
                    alert(e.message || 'Failed to save');
                  }
                }}
              >
                <div className={styles.grid}>
                  <label className={styles.label}><span>Full Name</span>
                    <input disabled={!editing} className={styles.input} value={form?.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>MRN</span>
                    <input disabled={!editing} className={styles.input} value={form?.mrn || ''} onChange={(e) => setForm({ ...form, mrn: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Age</span>
                    <input disabled={!editing} className={styles.input} type="number" min="0" value={form?.age || ''} onChange={(e) => setForm({ ...form, age: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Gender</span>
                    <select disabled={!editing} className={styles.input} value={form?.sex || 'M'} onChange={(e) => setForm({ ...form, sex: e.target.value })}>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </label>
                </div>
                <label className={styles.label}><span>Chief Complaint</span>
                  <textarea disabled={!editing} className={styles.textarea} rows={3} value={form?.chief_complaint || ''} onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} />
                </label>
                <label className={styles.label}><span>Chief Complaint History</span>
                  <textarea disabled={!editing} className={styles.textarea} rows={3} value={form?.chief_complaint_hx || ''} onChange={(e) => setForm({ ...form, chief_complaint_hx: e.target.value })} />
                </label>
                <div className={styles.grid2}>
                  <label className={styles.label}><span>Medical History</span>
                    <textarea disabled={!editing} className={styles.textarea} rows={3} value={form?.medical_hx || ''} onChange={(e) => setForm({ ...form, medical_hx: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Dental History</span>
                    <textarea disabled={!editing} className={styles.textarea} rows={3} value={form?.dental_hx || ''} onChange={(e) => setForm({ ...form, dental_hx: e.target.value })} />
                  </label>
                </div>
                <div className={styles.grid}>
                  <label className={styles.label}><span>Nationality</span>
                    <input disabled={!editing} className={styles.input} value={form?.nationality || ''} onChange={(e) => setForm({ ...form, nationality: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Occupation</span>
                    <input disabled={!editing} className={styles.input} value={form?.occupation || ''} onChange={(e) => setForm({ ...form, occupation: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Marital Status</span>
                    <input disabled={!editing} className={styles.input} value={form?.marital_status || ''} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} />
                  </label>
                </div>
                <div className={styles.actions}>
                  {!editing ? (
                    <button type="button" className={styles.btnGhost} onClick={() => setEditing(true)}>Edit</button>
                  ) : (
                    <>
                      <button type="button" className={styles.btnGhost} onClick={() => { setEditing(false); setForm({
                        full_name: patient.full_name || '', mrn: patient.mrn || '', age: patient.age || '', sex: patient.sex || 'M', chief_complaint: patient.chief_complaint || '', chief_complaint_hx: patient.chief_complaint_hx || '', medical_hx: patient.medical_hx || '', dental_hx: patient.dental_hx || '', nationality: patient.nationality || '', occupation: patient.occupation || '', marital_status: patient.marital_status || ''
                      }); }}>Cancel</button>
                      <button type="submit" className={styles.btnPrimary}>Save Changes</button>
                    </>
                  )}
                </div>
              </form>
            )}
            {activePlanId && (
              <div style={{ marginTop: 16 }}>
                <h3>Plan Info</h3>
                <div className={styles.grid}>
                  <label className={styles.label}><span>Starting Date</span>
                    <input type="date" disabled={!planEditing} className={styles.input} value={planMeta.starting_date || ''} onChange={(e)=> setPlanMeta({ ...planMeta, starting_date: e.target.value })} />
                  </label>
                  <label className={styles.label}><span>Academic Level</span>
                    <input disabled={!planEditing} className={styles.input} placeholder="e.g., PG1" value={planMeta.academic_level || ''} onChange={(e)=> setPlanMeta({ ...planMeta, academic_level: e.target.value })} />
                  </label>
                  {planMeta.case_number ? (
                    <label className={styles.label}><span>Case Number</span>
                      <div className={styles.value}>{planMeta.case_number}</div>
                    </label>
                  ) : null}
                </div>
                <div className={styles.actions}>
                  {!planEditing ? (
                    <button className={styles.btnGhost} onClick={()=> setPlanEditing(true)}>Edit</button>
                  ) : (
                    <>
                      <button className={styles.btnGhost} onClick={()=>{ const m=(plans[0]?.meta)||{}; setPlanMeta({ starting_date: m.starting_date||'', academic_level: m.academic_level||'', case_number: m.case_number||''}); setPlanEditing(false); }}>Cancel</button>
                      <button className={styles.btnPrimary} onClick={async ()=>{
                        try{
                          const updated = await apiFetch(`/plans/${activePlanId}`, { method: 'PATCH', body: { meta: {
                            starting_date: planMeta.starting_date || '',
                            academic_level: planMeta.academic_level || '',
                          } } }, getToken);
                          const m = updated.meta || {};
                          setPlans((prev)=> prev.map((p)=> p.id===updated.id? updated : p));
                          setPlanMeta({ starting_date: m.starting_date || '', academic_level: m.academic_level || '', case_number: m.case_number || ''});
                          setPlanEditing(false);
                        }catch(e){
                          alert(e.message||'Failed to save plan info');
                        }
                      }}>Save Changes</button>
                    </>
                  )}
                </div>
              </div>
            )}
            <ExamSection patientId={id} />
          </div>
        )}

        {tab === 'plan' && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Treatment Plan</h3>
              {!activePlanId && (
                <button className={styles.btnPrimary} onClick={() => setShowCreatePlan(true)}>+ Create Plan</button>
              )}
            </div>
            {plansLoading ? (
              <div className={styles.info}>Loading plan…</div>
            ) : !activePlanId ? (
              <div className={styles.info}>No plan yet. Create one to get started.</div>
            ) : (
              <PlanItemsEditor planId={activePlanId} />
            )}
          </div>
        )}

        {tab === 'assets' && activePlanId && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Assets</h3>
            <AssetsPanel patientId={id} />
          </div>
        )}

        {tab === 'generate' && activePlanId && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Generate</h3>
            <GeneratePanel patientId={id} />
          </div>
        )}
      </div>

      {showCreatePlan && (
        <PlanWizard
          patientId={id}
          onClose={() => setShowCreatePlan(false)}
          onCreated={(plan) => {
            setPlans((prev) => [plan, ...prev]);
            setShowCreatePlan(false);
          }}
        />
      )}
    </div>
  );
}

function ExamSection({ patientId }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    FaceSymmetry: '', FaceSwelling: '', Skin: '', LipsCompetence: '', TMJ_Clicking: '', TMJ_Pain: ''
  });
  const [status, setStatus] = useState('');
  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      try {
        const ex = await apiFetch(`/patients/${patientId}/exams?kind=extraoral_examination`, {}, getToken);
        if (ex && ex.details) setForm({ ...form, ...ex.details });
      } catch (e) {
        // ignore if none yet
      }
    })();
    // eslint-disable-next-line
  }, [patientId]);

  return (
    <div style={{ marginTop: 16 }}>
      <h3>Extraoral Examination</h3>
      <div className={styles.grid}>
        {['FaceSymmetry','FaceSwelling','Skin','LipsCompetence','TMJ_Clicking','TMJ_Pain'].map((k) => (
          <label key={k} className={styles.label}><span>{k.replace('_',' ')}</span>
            <input disabled={!editing} className={styles.input} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </label>
        ))}
      </div>
      <div className={styles.actions}>
        {!editing ? (
          <button className={styles.btnGhost} onClick={() => setEditing(true)}>Edit</button>
        ) : (
          <>
            <button className={styles.btnGhost} onClick={() => setEditing(false)}>Cancel</button>
            <button className={styles.btnPrimary} onClick={async () => {
              try {
                await apiFetch(`/patients/${patientId}/exams`, { method: 'PUT', body: { kind: 'extraoral_examination', details: form } }, getToken);
                setStatus('Saved');
                setEditing(false);
              } catch (e) {
                setStatus(e.message || 'Failed to save');
              }
            }}>Save</button>
          </>
        )}
        {status && <span style={{ marginLeft: 8, color: '#4b5563' }}>{status}</span>}
      </div>
    </div>
  );
}
